import { useState } from "react";
import { Target, Copy, AlertCircle, CheckCircle, HelpCircle, Eye } from "lucide-react";
import styles from "./ButtonPositionEditor.module.css";

export default function ButtonPositionEditor({
    buttonConfig,
    positionData,
    onUpdate,
    errors,
    storeUrl = window.location.origin, // Replace useShopInfo with prop
    cf_username = '' // Replace useShopInfo with prop
}) {

    const [targetName, setTargetName] = useState(positionData?.test?.targetName || '');
    const [targetSelector, setTargetSelector] = useState(positionData?.test?.targetSelector || '');
    const [position, setPosition] = useState(positionData?.test?.position || 'append');
    const [localErrors, setLocalErrors] = useState({});
    const [showHelperModal, setShowHelperModal] = useState(false);
    const [testId] = useState(() => `test_${Date.now()}`);

    // Safety check for buttonConfig
    if (!buttonConfig) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    <AlertCircle size={48} className={styles.emptyIcon} />
                    <h4>No Button Selected</h4>
                    <p>Please go back to the "Select Button" tab and choose a button first.</p>
                </div>
            </div>
        );
    }
    

    const validateSelector = (selector) => {
        if (!selector.trim()) {
            return 'CSS selector is required';
        }

        try {
            document.querySelector(selector);
            return null;
        } catch (error) {
            return 'Invalid CSS selector format';
        }
    };

    const handleTargetNameChange = (value) => {
        setTargetName(value);
        if (localErrors.targetName && value.trim()) {
            setLocalErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.targetName;
                return newErrors;
            });
        }
    };

    const handleTargetSelectorChange = (value) => {
        setTargetSelector(value);

        if (value.trim()) {
            const error = validateSelector(value);
            setLocalErrors(prev => ({ ...prev, targetSelector: error }));
        } else {
            setLocalErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.targetSelector;
                return newErrors;
            });
        }
    };

    const handlePositionChange = (value) => {
        setPosition(value);
    };

    const hasValidInput = () => {
        const nameValid = targetName.trim().length > 0;
        const selectorValid = targetSelector.trim().length > 0 && !localErrors.targetSelector;
        return nameValid && selectorValid;
    };

    const handleSubmit = () => {
        const newErrors = {};

        if (!targetName.trim()) {
            newErrors.targetName = 'Target container name is required';
        }

        const selectorError = validateSelector(targetSelector);
        if (selectorError) {
            newErrors.targetSelector = selectorError;
        }

        setLocalErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const positionConfig = {
                original: positionData?.original || {
                    targetName: '',
                    targetSelector: '',
                    position: 'current'
                },
                test: {
                    targetName: targetName.trim(),
                    targetSelector: targetSelector.trim(),
                    position: position
                }
            };

            onUpdate(positionConfig);
        }
    };

    const openSelectorTool = () => {
        setShowHelperModal(true);

        // Use specific page URL from button config
        const targetUrl = buttonConfig.pageUrl || storeUrl;

        // Generate preview URL using specific page
        const previewParams = new URLSearchParams({
            cf_element_selection: 'true',
            test_id: testId,
            mode: 'target_selector',
            return_url: window.location.href
        });

        // Parse the target URL and add query params
        const url = new URL(targetUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();

        // Open in new tab
        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        // Listen for selection results
        startListeningForSelection();
    };

    const openPositionPreview = () => {
        // Use specific page URL from button config
        const targetUrl = buttonConfig.pageUrl || storeUrl;

        // Generate preview URL with position test data
        const previewParams = new URLSearchParams({
            cf_position_preview: 'true',
            test_id: testId,
            button_selector: buttonConfig.selector,
            button_name: buttonConfig.name,
            target_selector: targetSelector,
            target_name: targetName,
            position_method: position,
            return_url: window.location.href
        });

        // Parse the target URL and add query params
        const url = new URL(targetUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();

        // Open in new tab
        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        showToast('🔍 Preview opened! Check the new tab to see how your button will look.');
    };

    const startListeningForSelection = () => {
        const handleSelection = (event) => {
            if (event.data?.type === 'CF_ELEMENT_SELECTED' && event.data?.mode === 'target_selector') {
                const { selector, elementText } = event.data;

                // Auto-fill the form
                setTargetName(elementText || 'Selected Container');
                setTargetSelector(selector);
                setShowHelperModal(false);

                // Clear any errors
                setLocalErrors({});

                // Remove event listener
                window.removeEventListener('message', handleSelection);
            }
        };

        window.addEventListener('message', handleSelection);

        // Cleanup after 10 minutes
        setTimeout(() => {
            window.removeEventListener('message', handleSelection);
        }, 600000);
    };

    const copyQueryParams = () => {
        const params = `?cf_element_selection=true&test_id=${testId}&mode=target_selector`;
        navigator.clipboard.writeText(params);
        showToast('✅ Query parameters copied to clipboard!');
    };

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 10000;
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            document.body.removeChild(toast);
        }, 3000);
    };

    const positionOptions = [
        { value: 'prepend', label: 'Insert as First Child', description: 'Add as the first element inside the container' },
        { value: 'append', label: 'Insert as Last Child', description: 'Add as the last element inside the container' },
        { value: 'before', label: 'Insert Before Container', description: 'Add just before the container element' },
        { value: 'after', label: 'Insert After Container', description: 'Add just after the container element' }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h4 className={styles.title}>Button Position Configuration</h4>
                <p className={styles.description}>
                    Choose where to move your <strong>{buttonConfig.name}</strong> button on the page.
                </p>
            </div>

            <div className={styles.form}>
                {/* Current Button Info */}
                <div className={styles.currentButton}>
                    <h5 className={styles.currentButtonTitle}>Button to Move:</h5>
                    <div className={styles.buttonInfo}>
                        <span className={styles.buttonName}>{buttonConfig.name}</span>
                        <code className={styles.buttonSelector}>{buttonConfig.selector}</code>
                    </div>
                </div>

                {/* Target Container Name */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Target Container Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={targetName}
                        onChange={(e) => handleTargetNameChange(e.target.value)}
                        placeholder="e.g., Product Sidebar"
                        className={`${styles.input} ${localErrors.targetName ? styles.error : ''}`}
                    />
                    {localErrors.targetName && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{localErrors.targetName}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        Descriptive name for where you want to move the button
                    </div>
                </div>

                {/* Target Container Selector */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Target Container CSS Selector <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.selectorInputWrapper}>
                        <input
                            type="text"
                            value={targetSelector}
                            onChange={(e) => handleTargetSelectorChange(e.target.value)}
                            placeholder="e.g., .product-sidebar"
                            className={`${styles.input} ${styles.selectorInput} ${localErrors.targetSelector ? styles.error : ''}`}
                        />
                        <button
                            type="button"
                            onClick={openSelectorTool}
                            className={styles.helperButton}
                            title="Open container finder tool"
                        >
                            <Target size={16} />
                            Find Container
                        </button>
                    </div>
                    {localErrors.targetSelector && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{localErrors.targetSelector}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        CSS selector for the container where you want to move the button
                        <button
                            type="button"
                            className={styles.helpLink}
                            onClick={() => setShowHelperModal(true)}
                        >
                            <HelpCircle size={12} />
                            Need help?
                        </button>
                    </div>
                </div>

                {/* Position Method */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>Position Method</label>
                    <div className={styles.positionGrid}>
                        {positionOptions.map((option) => (
                            <label key={option.value} className={`${styles.positionOption} ${position === option.value ? styles.selected : ''}`}>
                                <input
                                    type="radio"
                                    name="position"
                                    value={option.value}
                                    checked={position === option.value}
                                    onChange={(e) => handlePositionChange(e.target.value)}
                                    className={styles.radioInput}
                                />
                                <div className={styles.positionContent}>
                                    <div className={styles.positionLabel}>{option.label}</div>
                                    <div className={styles.positionDescription}>{option.description}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Preview Summary - Simplified */}
                {hasValidInput() && (
                    <div className={styles.previewSummary}>
                        <h5 className={styles.previewTitle}>Position Summary:</h5>
                        <div className={styles.previewText}>
                            Move <strong>{buttonConfig.name}</strong> to <strong>{targetName}</strong> using <em>{position}</em> method
                        </div>
                        <div className={styles.technicalSummary}>
                            <code>{`document.querySelector('${targetSelector}').${position}(buttonElement)`}</code>
                        </div>
                    </div>
                )}

                {/* Submit Button */}
                <div className={styles.actions}>
                    {hasValidInput() && (
                        <button
                            onClick={openPositionPreview}
                            className={styles.previewButton}
                            title="Preview how the button will look in the new position"
                        >
                            <Eye size={16} />
                            Preview Changes
                        </button>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={!hasValidInput()}
                        className={styles.submitButton}
                    >
                        <CheckCircle size={16} />
                        Apply Position Changes
                    </button>
                </div>
            </div>

            {/* Helper Modal */}
            {showHelperModal && (
                <div className={styles.modal}>
                    <div className={styles.modalOverlay} onClick={() => setShowHelperModal(false)} />
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>🔍 Container Finder Tool</h3>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepContent}>
                                    <h4>Preview window opened</h4>
                                    <p>A new tab opened with your store. If it didn't open, check your popup blocker.</p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepContent}>
                                    <h4>Visit the right page</h4>
                                    <p>Navigate to where you want to move the button. Add these query parameters to any URL:</p>

                                    <div className={styles.copyBox}>
                                        <code>?cf_element_selection=true&test_id={testId}&mode=target_selector</code>
                                        <button onClick={copyQueryParams} className={styles.copyButton}>
                                            <Copy size={14} />
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepContent}>
                                    <h4>Click target container</h4>
                                    <p>Click on the container where you want to move your button. The selector will be automatically filled in.</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.gotItButton}
                            >
                                Got it!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}